import React, { useState, useEffect } from 'react';
import { useShopifyProduct } from '../../../../../api/shopify/ShopifyProduct';
import { useLocalisationContext } from '../../context/LocalisationContext';
import { ProductFulfilmentPdp } from './ProductFulfilmentPdp';
import { StoreLocation } from '../../utils/types';
import {
  FULFILMENT_REQUEST_OPTIONS_IN_STORE,
  FULFILMENT_REQUEST_OPTIONS_DELIVERY,
} from '@jbhi-fi/apis/src/fulfilment/utils';
import {
  getIsPremiumDeliveryAvailable,
  transformDeliveryOptions,
  transformPickupOptions,
} from '../../utils/productFulfilmentUtils';
import { DEFAULT_CACHE_LIFETIME_SECONDS } from './constants';
import { useQuery, useQueryClient } from 'react-query';
import { ShopifyProductTransformer } from '@jbhi-fi/components/src/Shopify/transformers/ShopifyProductTransformer';
import { useFulfilmentOptions } from '@jbhi-fi/components/src/hooks/useFulfilmentOptions/useFulfilmentOptions';
import { AvailabilityVariantType } from '../../../availability/availability-wrapper/AvailabilityWrapper';

export interface ProductFulfilmentInfoWrappedProps {
  setIsAvailabilityWrapperOpen?: () => void;
  setActiveAvailabilityVariant?: (variant: AvailabilityVariantType) => void;
  LIAStore?: StoreLocation;
  isLIA?: boolean;
  shouldShowDisplayModelStatus?: boolean;
}

export const ProductFulfilmentInfoWrapped = ({
  setIsAvailabilityWrapperOpen,
  setActiveAvailabilityVariant,
  LIAStore,
  isLIA,
  shouldShowDisplayModelStatus = false,
}: ProductFulfilmentInfoWrappedProps) => {
  const {
    shouldCacheAutoRefresh,
    cacheLifetimeSeconds,
    requireFetchManually,
    selectedStore,
    deliveryLocation,
    setYourStoreFulfilmentOptions,
  } = useLocalisationContext();
  const shouldUseCache = !!shouldCacheAutoRefresh;
  const cacheTime = shouldUseCache ? cacheLifetimeSeconds * 1000 || DEFAULT_CACHE_LIFETIME_SECONDS * 1000 : Infinity;
  const { shopify, productMetafields, marketplaceSellers } = useShopifyProduct();
  const { ProductTags, Availability, Product } = productMetafields;
  const isPreOrder: boolean = Availability?.AvailabilityStatement === 'Pre-order online';
  const isBulky: boolean = Availability?.DeliveryConstraint === 'BulkyDeliveryZones';
  const isBackOrder: boolean = Availability?.AvailabilityStatement === 'Back order available';
  const isDeliveryOnly: boolean = Availability?.DeliveryOnly;
  const isDeliveryAvailableMetafield = Availability?.CanBuyOnline;
  const isInStoreOnly =
    ProductTags?.AllCalculatedProductTags && ProductTags?.AllCalculatedProductTags?.includes('In Store Only');
  const isLocationSet = !!deliveryLocation?.displayName;
  const maxDistance = window.Jbhifi?.pdpAlerts?.availabilityMaxDistance;
  const [isFetchDisabled, setIsFetchDisabled] = useState<boolean>(!!requireFetchManually);
  const sku = productMetafields?.Id;
  const marketplaceSeller = ShopifyProductTransformer.toMarketplaceSeller({
    product: shopify,
    marketplaceSellers,
    onlineProduct: productMetafields,
  });
  const queryClient = useQueryClient();
  const isDeliveryFetchReady = !!(productMetafields?.Id && deliveryLocation?.postcode && !isInStoreOnly);

  const preSelectedStore = LIAStore || selectedStore;
  const isPickupFetchReady = !!(
    productMetafields?.Id &&
    preSelectedStore?.postcode &&
    ((isLIA && LIAStore) || (!isLIA && selectedStore))
  );
  const freightPrice = Product?.FreightPrice;

  const lineItems = [
    {
      SKU: sku?.toString() ?? '',
      LineItemFreightPrice: freightPrice,
      TieredIds: marketplaceSeller?.tieredIds?.length ? marketplaceSeller.tieredIds : undefined,
      Quantity: 1,
      GiftCardType: undefined,
    },
  ];

  const {
    isLoading: isFulfilmentLoading,
    error,
    data,
  } = useFulfilmentOptions({
    addresses: [
      isDeliveryFetchReady
        ? {
            countryCode: window.Jbhifi.storeLocation,
            postcode: deliveryLocation?.postcode,
            suburb: deliveryLocation?.suburb,
            requestTypes: FULFILMENT_REQUEST_OPTIONS_DELIVERY,
          }
        : null,
      isPickupFetchReady
        ? {
            countryCode: window.Jbhifi.storeLocation,
            postcode: preSelectedStore?.postcode,
            suburb: preSelectedStore?.suburb,
            requestTypes: FULFILMENT_REQUEST_OPTIONS_IN_STORE,
          }
        : null,
    ],
    isEnabled: !isFetchDisabled,
    lineItems,
    cacheTime: cacheTime,
    staleTime: cacheTime,
  });

  const [deliveryOptionsResponse, pickupFulfilmentResponse] = data ?? [];

  const deliveryData = deliveryOptionsResponse?.delivery
    ? transformDeliveryOptions({
        deliveryOptions: deliveryOptionsResponse.delivery,
        isBulky,
        isBackOrder,
      })
    : undefined;

  const {
    isFetching: isPickupLoading,
    error: pickupError,
    data: pickupData,
  } = useQuery({
    queryKey: [
      'ProductInfoFulfilmentWrapped',
      'pickup',
      pickupFulfilmentResponse,
      preSelectedStore,
      isPreOrder,
      isBulky,
      isBackOrder,
      maxDistance,
      shouldShowDisplayModelStatus,
    ],
    queryFn: () =>
      pickupFulfilmentResponse
        ? transformPickupOptions({
            pickupOptions: pickupFulfilmentResponse,
            selectedStore: preSelectedStore,
            isPreOrder,
            isBulky,
            isBackOrder,
            maxDistance,
            shouldShowDisplayModelStatus,
            queryClient,
          })
        : undefined,
    enabled: !!pickupFulfilmentResponse,
  });

  const isError = !!(error || pickupError);
  const delivery = {
    yourSuburb: deliveryLocation?.displayName,
    options: deliveryData,
  };
  const isLoading = isFulfilmentLoading || isPickupLoading;

  const handleFetchClick = () => {
    setIsFetchDisabled(false);
  };

  useEffect(() => {
    if (pickupData?.yourStore) {
      const { name, isCncAvailable, isInStoreAvailable, isOnlyDisplayAvailable, isAwaitingStock } =
        pickupData.yourStore;
      setYourStoreFulfilmentOptions({
        name,
        isCncAvailable,
        isInStoreAvailable,
        isOnlyDisplayAvailable,
        importantMessaging: pickupData?.importantMessaging,
        isAwaitingStock,
      });
    }
  }, [pickupData?.yourStore]);
  return (
    <ProductFulfilmentPdp
      isLocationSet={isLocationSet}
      isLoading={isLoading}
      isError={isError}
      delivery={delivery}
      isDeliveryAvailableMetafield={isDeliveryAvailableMetafield}
      isDeliveryOnly={isDeliveryOnly}
      pickup={pickupData}
      isInStoreOnly={isInStoreOnly}
      isPreOrder={isPreOrder}
      isBackOrder={isBackOrder}
      isPremiumDeliveryAvailable={getIsPremiumDeliveryAvailable(deliveryData || [])}
      setIsAvailabilityWrapperOpen={setIsAvailabilityWrapperOpen}
      setActiveAvailabilityVariant={setActiveAvailabilityVariant}
      isLIA={isLIA}
      isAwaitingManualFetch={isFetchDisabled}
      handleFetchClick={handleFetchClick}
      marketplaceSellerName={
        ShopifyProductTransformer.toMarketplaceSellerCalloutProps({
          marketplaceSellers,
          product: shopify,
          onlineProduct: productMetafields,
        })?.sellerName
      }
    />
  );
};
